import {
  makeStyles,
  tokens,
  Text,
  Card,
  CardHeader,
  Badge,
} from '@fluentui/react-components';
import type { TeamsCallInfo } from '../../types';

const useStyles = makeStyles({
  card: {
    minHeight: '200px',
  },
  cardContent: {
    padding: tokens.spacingHorizontalM,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  label: {
    color: tokens.colorNeutralForeground3,
  },
  value: {
    fontWeight: tokens.fontWeightSemibold,
  },
  badgeRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap' as const,
  },
});

function formatCallType(callType: string | null): string {
  if (!callType) return '-';
  switch (callType) {
    case 'peerToPeer': return 'Peer to Peer';
    case 'groupCall': return 'Group Call';
    default: return callType;
  }
}

function formatMatchMethod(method: string | null): string {
  if (!method) return '-';
  switch (method) {
    case 'correlation_id': return 'Correlation ID';
    case 'phone_time': return 'Phone + Time';
    default: return method;
  }
}

interface TeamsParticipantsCardProps {
  teamsData: TeamsCallInfo;
}

export function TeamsParticipantsCard({ teamsData }: TeamsParticipantsCardProps) {
  const styles = useStyles();

  return (
    <Card className={styles.card}>
      <CardHeader header={<Text weight="semibold">Teams Call Info</Text>} />
      <div className={styles.cardContent}>
        <div className={styles.infoGrid}>
          {teamsData.organizer_display_name && (
            <>
              <Text className={styles.label}>Organizer</Text>
              <Text className={styles.value}>
                {teamsData.organizer_display_name}
                {teamsData.organizer_email && (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginLeft: '4px' }}>
                    ({teamsData.organizer_email})
                  </Text>
                )}
              </Text>
            </>
          )}

          {teamsData.teams_user_display_name && (
            <>
              <Text className={styles.label}>Teams User</Text>
              <Text className={styles.value}>
                {teamsData.teams_user_display_name}
                {teamsData.teams_user_email && (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginLeft: '4px' }}>
                    ({teamsData.teams_user_email})
                  </Text>
                )}
              </Text>
            </>
          )}

          <Text className={styles.label}>Call Type</Text>
          <Text className={styles.value}>{formatCallType(teamsData.call_type)}</Text>

          {teamsData.modalities && (
            <>
              <Text className={styles.label}>Modalities</Text>
              <div className={styles.badgeRow}>
                {teamsData.modalities.split(',').map((m) => (
                  <Badge key={m.trim()} appearance="outline" size="small" style={{ textTransform: 'capitalize' }}>
                    {m.trim()}
                  </Badge>
                ))}
              </div>
            </>
          )}

          <Text className={styles.label}>Match</Text>
          <Text className={styles.value}>
            {formatMatchMethod(teamsData.match_method)}
            {teamsData.match_confidence !== null && (
              <Badge
                appearance="tint"
                color={teamsData.match_confidence >= 0.9 ? 'success' : 'warning'}
                size="small"
                style={{ marginLeft: '8px' }}
              >
                {Math.round(teamsData.match_confidence * 100)}%
              </Badge>
            )}
          </Text>

          {teamsData.trunk_fqdn && (
            <>
              <Text className={styles.label}>Trunk FQDN</Text>
              <Text className={styles.value} style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {teamsData.trunk_fqdn}
              </Text>
            </>
          )}

          {teamsData.media_bypass_enabled !== null && (
            <>
              <Text className={styles.label}>Media Bypass</Text>
              <Badge appearance="outline" size="small" color={teamsData.media_bypass_enabled ? 'success' : 'informative'}>
                {teamsData.media_bypass_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </>
          )}

          {teamsData.final_sip_code !== null && (
            <>
              <Text className={styles.label}>SIP Code</Text>
              <Text className={styles.value}>
                {teamsData.final_sip_code}
                {teamsData.final_sip_code_phrase && ` - ${teamsData.final_sip_code_phrase}`}
              </Text>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
